import * as React from "react";
import {
    Modal, Box, Typography, IconButton, Grid, TextField, Select, MenuItem,
    Divider,
    Skeleton,
    Autocomplete
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CtaButton from "../buttonWithArrow";
import { useDispatch, useSelector } from '../../redux/store'
import LockIcon from "@mui/icons-material/Lock";
import { countries } from '../../data/countries';
import { CurrencySimple, useResponsiveValue } from "../../utils/helper";
import { getOverAllAmountList } from "../../redux/slices/investments";

function JetSkeletonCard() {
    return (
        <>
            <Box>
                <Skeleton
                    variant="rectangular"
                    height={100}
                    sx={{ borderRadius: "12px" }}
                />
                <Skeleton
                    variant="text"
                    width="60%"
                    height={18}
                    sx={{ mx: "auto", mt: 1 }}
                />
            </Box>
        </>
    );
}

// ---------- helpers ----------
const CURRENCY = {
    USD: { sign: "$", rate: 1, type: "string" },
    AED: { sign: "images/bannerImage/1758795455186.svg", rate: 3.67, type: "image" },
    // EUR: { sign: "€", rate: 0.92 },
};

// pill look
const pillInputSx = {
    "& .MuiOutlinedInput-root": { borderRadius: 999 },
};

// ---------- main component ----------
export default function InvestmentSimulatorFlow({
    open,
    onClose,
    onInvest, // (payload) => void
    values,
    setValues,
    formLoader, priceData
}) {
    const dispatch = useDispatch();
    const [step, setStep] = React.useState(1);
    const [amount, setAmount] = React.useState("");
    const [currency, setCurrency] = React.useState("AED");
    const [selected, setSelected] = React.useState(null);
    const [eligible, setEligible] = React.useState([]);
    const [loader, setLoader] = React.useState(false);
    const { overAllAmountRes } = useSelector(
        state => state.investment
    );
    const parsedAmount = Number(String(amount).replace(/,/g, "")) || 0;
    const [error, setError] = React.useState({
        name: false,
        email: false,
        countryCode: false,
        phone: false
    });

    React.useEffect(() => {
        closeAll();
    }, [])

    const resetAll = () => {
        setStep(1);
        setSelected(null);
        setValues({ ...values })
        setValues({ ...values })
        setAmount("");
        setCurrency("AED");
    };

    const closeAll = () => { onClose(); setTimeout(resetAll, 300); };

    const errors = React.useMemo(() => {
        const errs = {};

        // User name
        if (!values.name.trim()) {
            errs.name = "Name is required";
        }

        // Email
        if (!values.email.trim()) {
            errs.email = "Email is required";
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(values.email)) {
                errs.email = "Enter a valid email address";
            }
        }

        // Phone
        if (!values.phone.trim()) {
            errs.phone = "Phone number is required";
        } else {
            const phoneRegex = /^[0-9]{6,15}$/; // 6–15 digits
            if (!phoneRegex.test(values.phone)) {
                errs.phone = "Enter a valid phone number";
            }
        }

        return errs;
    }, [values]);

    const isValid = React.useMemo(() => Object.keys(errors).length === 0, [errors]);

    const handleSubmit = () => {
        setError(
            {
                name: true,
                email: true,
                countryCode: true,
                phone: true,
                itemId: true
            }
        );
        if (!isValid) return;
        setError(
            {
                name: false,
                email: false,
                countryCode: false,
                phone: false,
                itemId: false
            }
        );
        onInvest?.({ ...values, currency: currency });
    };

    const responsiveSelectFont = useResponsiveValue({ xs: 15, sm: 15, lg: 15 });

    React.useEffect(() => {
        if (amount && currency) {
            const obj = {
                amount: amount,
                currency: currency
            }
            setLoader(true);
            dispatch(getOverAllAmountList(obj))
        }
    }, [amount, currency])

    React.useEffect(() => {
        if (overAllAmountRes) {
            setLoader(false);
            if (overAllAmountRes?.status === 1) {
                setEligible(overAllAmountRes?.responseData)
            } else {
                setEligible([]);
            }
        }
    }, [overAllAmountRes])

    return (
        <Modal open={open} onClose={closeAll}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
            <Box sx={{ position: "relative", width: 820, maxWidth: "95vw", bgcolor: "#fff", borderRadius: 4, p: 3 }}>
                {/* close */}
                <IconButton onClick={closeAll} sx={{ position: "absolute", top: 8, right: 8 }}>
                    <CloseRoundedIcon />
                </IconButton>

                {/* ---------------------- STEP HEADER ---------------------- */}
                {step !== 2 ? (
                    <>
                        <Typography className="jura" sx={{ fontWeight: 800, fontSize: 24, textAlign: "center" }}>
                            Investment calculator simulator
                        </Typography>
                        <Typography sx={{ color: "#6b7280", textAlign: "center", mt: 1, mb: 2 }}>
                            Enter your amount to see tailored options where you can invest and grow with RSL.
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography className="jura" sx={{ fontWeight: 800, fontSize: 24, textAlign: "center" }}>
                            {selected?.title}
                        </Typography>
                        <Typography sx={{ color: "#6b7280", textAlign: "center", mt: 1, mb: 2 }}>
                            These are the approx. values you can get with the product that you choosed
                        </Typography>
                    </>
                )}

                {/* ---------------------- STEP 1 & 2 ---------------------- */}
                {step !== 2 && step === 1 && (
                    <Box sx={{ border: "1px solid #dddce0", borderRadius: 2.5, p: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={8}>
                                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Amount</Typography>
                                <Select
                                    fullWidth
                                    displayEmpty
                                    value={amount ?? ""}
                                    onChange={(e) => setAmount(e.target.value)}
                                    sx={{
                                        ...pillInputSx,
                                        "& fieldset": { borderRadius: 999 },
                                    }}
                                    renderValue={(selected) => {
                                        if (!selected) {
                                            return (
                                                <Box sx={{ color: "rgba(0,0,0,0.5)" }}>
                                                    Select Amount
                                                </Box>
                                            );
                                        }
                                        return (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                {CurrencySimple(
                                                    null,
                                                    null,
                                                    responsiveSelectFont,
                                                    CURRENCY[currency]?.type,
                                                    CURRENCY[currency]?.type === "string"
                                                        ? CURRENCY[currency]?.sign
                                                        : null
                                                )}
                                                {selected}
                                            </Box>
                                        );
                                    }}
                                >
                                    {/* placeholder item (hidden but keeps accessibility) */}
                                    <MenuItem value="">
                                        <em>Select Amount</em>
                                    </MenuItem>

                                    {priceData?.map((c) => (
                                        <MenuItem key={c} value={c}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                {CurrencySimple(
                                                    null,
                                                    null,
                                                    responsiveSelectFont,
                                                    CURRENCY[currency]?.type,
                                                    CURRENCY[currency]?.type === "string"
                                                        ? CURRENCY[currency]?.sign
                                                        : null
                                                )}
                                                {c}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                                {/* <TextField
                                    fullWidth
                                    value={amount}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(/\D/g, "");
                                        setAmount(raw);
                                    }}
                                    placeholder="Enter Amount you want to invest"
                                    sx={pillInputSx}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                {CURRENCY[currency]?.type === "string" ? (
                                                    CURRENCY[currency]?.sign
                                                ) : (
                                                    <img
                                                        src={`${process.env.IMAGE_BASE_URL}/${String(CURRENCY[currency]?.sign)}`}
                                                        alt="currency"
                                                        style={{
                                                            width: "1em",
                                                            height: "1em",
                                                            objectFit: "contain",
                                                        }}
                                                    />
                                                )}
                                            </InputAdornment>
                                        ),
                                    }}
                                /> */}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Currency</Typography>
                                <Select
                                    fullWidth
                                    value={currency}
                                    onChange={(e) => setCurrency(e.target.value)}
                                    sx={{ ...pillInputSx, "& fieldset": { borderRadius: 999 } }}
                                    renderValue={(v) => (
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            {CURRENCY[v].type === "string" ? (
                                                <>{CURRENCY[v].sign}</>
                                            ) : (
                                                <img
                                                    src={`${process.env.IMAGE_BASE_URL}/${String(CURRENCY[v].sign)}`}
                                                    alt="currency"
                                                    style={{ width: "1em", height: "1em", objectFit: "contain" }}
                                                />
                                            )}
                                            {v}
                                        </Box>
                                    )}
                                >
                                    {Object.keys(CURRENCY).map((c) => (
                                        <MenuItem key={c} value={c}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                {CURRENCY[c].type === "string" ? (
                                                    <>{CURRENCY[c].sign}</>
                                                ) : (
                                                    <img
                                                        src={`${process.env.IMAGE_BASE_URL}/${String(CURRENCY[c].sign)}`}
                                                        alt="currency"
                                                        style={{ width: "1em", height: "1em", objectFit: "contain" }}
                                                    />
                                                )}
                                                {c}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                        </Grid>

                        {parsedAmount > 0 && (
                            <>
                                <Divider sx={{ my: 2, borderColor: "#a1a1a1" }} />
                                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                                    You can invest in these categories with this investment amount. <Box component="span" sx={{ color: "#9CA3AF", fontWeight: 500 }}>(Choose one to continue)</Box>
                                </Typography>

                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 2,
                                        overflowX: "auto",
                                        py: 1,
                                        px: 1,
                                        mt: 2,
                                        "&::-webkit-scrollbar": {
                                            height: 6,
                                        },
                                        "&::-webkit-scrollbar-thumb": {
                                            backgroundColor: "#ccc",
                                            borderRadius: 3,
                                        },
                                    }}
                                >
                                    {loader &&
                                        [...Array(6)].map((_, i) => (
                                            <Box key={i} sx={{ flex: "0 0 auto", width: 200 }}>
                                                <JetSkeletonCard />
                                            </Box>
                                        ))}

                                    {!loader && eligible?.length === 0 && (
                                        <Box sx={{ color: "#9CA3AF", textAlign: "center", py: 2, flex: "0 0 100%" }}>
                                            No options match this amount yet. Try a higher amount.
                                        </Box>
                                    )}

                                    {
                                        !loader &&
                                        // assume values = { itemId?: string, itemInfo?: any }
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 2,
                                                overflowX: "auto",
                                                py: 1,
                                                px: 1,
                                                mt: 2,
                                                "&::-webkit-scrollbar": { height: 6 },
                                                "&::-webkit-scrollbar-thumb": { backgroundColor: "transparent", borderRadius: 3 },
                                            }}
                                        >
                                            {eligible?.map((b) => {
                                                // const isLocked = !!b?.locked;
                                                const isLocked = b?.availabilityStatus !== 1;
                                                const isActive = values?.itemId === b?._id;          // <-- active check

                                                return (
                                                    <Box key={b._id ?? b.name} sx={{ flex: "0 0 auto" }}>
                                                        <Box
                                                            role="button"
                                                            tabIndex={0}
                                                            aria-pressed={isActive}
                                                            onClick={() => {
                                                                if (isLocked) return;
                                                                setValues({ ...values, itemId: b._id, itemInfo: b });
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (!isLocked && (e.key === "Enter" || e.key === " ")) {
                                                                    setValues({ ...values, itemId: b._id, itemInfo: b });
                                                                }
                                                            }}
                                                            sx={{
                                                                width: 130,
                                                                height: 120,
                                                                borderRadius: 4,
                                                                overflow: "hidden",
                                                                position: "relative",
                                                                cursor: isLocked ? "not-allowed" : "pointer",
                                                                filter: isLocked ? "grayscale(80%) brightness(0.6)" : "none",
                                                                transition: "transform .2s, box-shadow .2s",
                                                                // ---- active styling ----
                                                                border: "3px solid transparent",
                                                                background: isActive
                                                                    ? "linear-gradient(#fff,#fff) padding-box, linear-gradient(180deg,#8AD9D9 0%, #0D8196 100%) border-box"
                                                                    : "#fff",
                                                                // boxShadow: isActive ? "0 6px 16px rgba(13,129,150,.25)" : "0 2px 8px rgba(0,0,0,.06)",
                                                                transform: isActive ? "scale(1.02)" : "none",
                                                                "&:hover": { transform: isLocked ? "none" : "scale(1.05)" },
                                                            }}
                                                        >
                                                            <img
                                                                src={`${process.env.IMAGE_BASE_URL}/${b?.backgroundImage}`}
                                                                alt={b.name}
                                                                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                            />

                                                            {isLocked && (
                                                                <Box
                                                                    sx={{
                                                                        position: "absolute",
                                                                        inset: 0,
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        bgcolor: "rgba(0,0,0,0.4)",
                                                                    }}
                                                                >
                                                                    <LockIcon sx={{ color: "#fff", fontSize: 36 }} />
                                                                </Box>
                                                            )}
                                                        </Box>

                                                        <Typography
                                                            sx={{
                                                                textAlign: "center",
                                                                mt: 1,
                                                                fontWeight: isActive ? 800 : 600,
                                                                fontSize: 13,
                                                                width: 120,
                                                                color: isActive ? "#0D8196" : "#111827",
                                                            }}
                                                        >
                                                            {b?.name ?? b?.title}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>

                                    }

                                </Box>
                                {
                                    error?.itemId &&
                                    <Box sx={{ display: "flex", justifyContent: "end", mt: 2, color: "red", fontSize: "12px" }}>
                                        Select a Service
                                    </Box>
                                }

                                <Box sx={{ display: "flex", justifyContent: "end", mt: 2 }}>
                                    <CtaButton label="Invest Now" onClick={() => {
                                        if (values?.itemId) {
                                            setStep(2);
                                            setError({
                                                ...error,
                                                itemId: false
                                            })
                                        } else {
                                            setError({
                                                ...error,
                                                itemId: true
                                            })
                                        }

                                    }} />
                                </Box>
                            </>
                        )}
                    </Box>
                )}

                {
                    step === 2 &&
                    <Box sx={{ p: 2 }}>
                        <Typography sx={{ fontWeight: 700 }}>Personal Details</Typography>
                        <Grid container spacing={2} sx={{ mt: 0 }}>
                            <Grid item xs={12} md={6}>
                                <Typography sx={{ fontWeight: 500, mb: 0.75 }}>Name</Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Enter Name"
                                    name="name"
                                    value={values.name}
                                    error={error.name && !!errors.name}
                                    helperText={error.name && errors.name ? errors.name : " "}
                                    onChange={(e) => setValues({ ...values, name: e.target.value })}
                                    sx={pillInputSx}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography sx={{ fontWeight: 500, mb: 0.75 }}>Email</Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Enter Email"
                                    value={values.email}
                                    name="email"
                                    error={error.email && !!errors.email}
                                    helperText={error.email && errors.email ? errors.email : " "}
                                    onChange={(e) => setValues({ ...values, email: e.target.value })}
                                    sx={pillInputSx}
                                />

                            </Grid>
                            <Grid item xs={12}>
                                <Typography sx={{ fontWeight: 500, mb: 0.75 }}>Phone</Typography>
                                <Box sx={{ display: "flex", mb: 2 }}>
                                    {/* Country Code Selector */}
                                    <Autocomplete
                                        disableClearable
                                        options={countries}
                                        getOptionLabel={(option) => `${option.code} (+${option.phone})`}
                                        filterOptions={(options, { inputValue }) =>
                                            options.filter(
                                                (option) =>
                                                    option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                                                    option.code.toLowerCase().includes(inputValue.toLowerCase()) ||
                                                    option.phone.includes(inputValue.replace("+", ""))
                                            )
                                        }
                                        value={countries.find((c) => c.phone === values.countryCode) || countries[0]}
                                        onChange={(e, value) => {
                                            if (value) setValues({ ...values, countryCode: value.phone });
                                        }}
                                        sx={{
                                            width: "35%",
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "999px 0 0 999px",
                                            },
                                        }}
                                        renderOption={(props, option) => (
                                            <li {...props}>
                                                <span style={{ marginRight: 8 }}>
                                                    {String.fromCodePoint(
                                                        ...[...option.code.toUpperCase()].map((c) => 127397 + c.charCodeAt())
                                                    )}
                                                </span>
                                                {option.code} (+{option.phone})
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "999px 0 0 999px",
                                                    },
                                                }}
                                            />
                                        )}
                                    />

                                    {/* Phone Number Input */}
                                    <TextField
                                        fullWidth
                                        placeholder="Enter Phone"
                                        value={values.phone}
                                        name="phone"
                                        onChange={(e) => setValues({ ...values, phone: e.target.value })}
                                        error={error.phone && !!errors.phone}
                                        helperText={error.phone && errors.phone ? errors.phone : " "}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "0 999px 999px 0", // rounded right side
                                                bgcolor: "#fff",
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>

                        </Grid>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                            <CtaButton label="Back" iconPosition="start" onClick={() => setStep(step - 1)} variant="secondary" sx={{ boxShadow: "none !important" }} endIconSrc="/img/header/VectorLeft.svg" />
                            <CtaButton label="Invest Now" loader={formLoader} onClick={handleSubmit} />
                        </Box>
                    </Box>
                }
            </Box>
        </Modal >
    );
}
